/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-tagless-components, getter-return, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import PermissionType from '@linc-technologies/linced-common/enums/events/permission-type';
import { getDefaultDatatype } from '@linc-technologies/linced-common/utils/dynamic-field-types';
import {
  autosaveCreateEditorId,
  autosaveTinyMCEConfig,
  manageAutosaveDraftsButton,
  removeAutosavedDrafts,
} from '@linc-technologies/linced-common/utils/tinymce';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { cancel, later } from '@ember/runloop';
import { observes } from '@ember-decorators/object';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class AddEditEntity extends Component {
  @service
  session;

  @service
  store;

  InputType = InputType;
  DataType = DataType;
  PermissionType = PermissionType;

  // inputTypes is a limited list - there are some types used for School Options which we don't want here.
  inputTypes = [
    { value: 3, label: 'Radio boxes' },
    { value: 4, label: 'Checkbox(es)' },
    { value: 9, label: 'Select' },
    { value: 11, label: 'Switch' },
    { value: 7, label: 'Number' },
  ];

  numericTypes = [
    { value: 3, label: 'Integer (whole numbers)' },
    { value: 4, label: 'Float (decimal numbers)' },
  ];
  selectDataTypes = [
    { value: 1, label: 'String' },
    { value: 3, label: 'Integer (whole numbers)' },
  ];

  @tracked
  numericType = null;

  @action
  setNumericType(value) {
    this.numericType = value;
  }

  @tracked
  inputType = null;

  @action
  setInputType(value) {
    this.inputType = value;
  }

  onRiskSaved() {}

  // TinyMCE editor
  // editorOptions returns an object for TinyMCE editor config with autosave enabled.
  // We need a unique element ID for specifying selectors and localStorage keys.
  @computed('editorId')
  get editorOptions() {
    return Object.freeze(autosaveTinyMCEConfig(this.editorId));
  }

  // editorId returns an element ID to assign to the editor's children
  @computed('session.person.id', 'additionalAutosavePrefix')
  get editorId() {
    let userId = this.session.person.get('id');

    return autosaveCreateEditorId(userId, this.additionalAutosavePrefix);
  }

  // the following prefixes are used in editorId
  // if a parent component or route adds it for more uniqueness.
  @tracked additionalAutosavePrefix = 'event';

  @observes('inputType.value')
  _updateInputType() {
    let type = this.get('inputType.value');
    if (!isEmpty(type) && !isEmpty(this.permission)) {
      this.permission.set('inputType', type);
      this.permission.set('dataType', getDefaultDatatype(type));
    }
  }

  get inputTypeHasOptions() {
    let inputType = this.get('inputType.value');
    switch (inputType) {
      case InputType.RADIO:
      case InputType.CHECKBOX:
      case InputType.SELECT:
        return true;
    }

    return false;
  }

  @tracked
  dialogOpen = false;
  @tracked
  errorMessage = '';
  @tracked
  permission = null;
  @tracked
  saveInProgress = false;
  @tracked
  template = null;
  @tracked
  templateDialogOpen = false;
  @tracked
  risk = null;
  @tracked
  riskDialogOpen = false;
  @tracked
  mitigation = null;
  @tracked
  mitigationDialogOpen = false;
  type = null;

  // risk.mitigations is an async hasMany, whose PromiseManyArray has no
  // map(); the assigned ids come straight from the relationship.
  // resolve the async hasMany relationship upfront.
  @computed('risk.mitigations.content.[]')
  get sortedRemainingMitigations() {
    if (!this.risk) {
      return [];
    }

    let assignedIds = new Set(this.risk.hasMany('mitigationIds').ids());
    return this.store
      .peekAll('events/mitigation')
      .filter((mitigation) => !assignedIds.has(mitigation.id))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @or('newOptionValue', 'newOptionLabel')
  newOption;

  @tracked
  isMobile;

  @tracked
  newOptionLabel;

  @tracked
  newOptionValue;

  @action
  setNewOptionValue(value) {
    this.newOptionValue = value;
  }

  @tracked
  timer;

  @tracked
  selectDataType;

  @action
  setSelectDataType(value) {
    this.selectDataType = value;
  }

  @computed('inputType.value', 'permission.options.[]')
  get lessThanTwoOptions() {
    if (
      [InputType.CHECKBOX, InputType.RADIO, InputType.SELECT].includes(
        this.get('inputType.value'),
      )
    ) {
      return this.permission.options.length < 2;
    }
  }

  @action
  closeDialog() {
    // Guards against a double-fire (e.g. the dialog's close button and a
    // backdrop click both invoking this for one interaction) -- without
    // it, the second call crashes on a null `permission` left by the
    // first, and would otherwise re-toggle dialogOpen back open.
    if (!this.permission) {
      return;
    }
    if (!this.permission.get('id')) {
      this.permission.deleteRecord();
      this.permission = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.permission);
    }
    this.toggleProperty('dialogOpen');
  }

  @action
  closeTemplateDialog() {
    // See closeDialog's comment -- same double-fire guard.
    if (!this.template) {
      return;
    }
    if (!this.template.get('id')) {
      this.template.deleteRecord();
      this.template = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.template);
    }
    this.toggleProperty('templateDialogOpen');
  }

  @action
  closeRiskDialog() {
    // See closeDialog's comment -- same double-fire guard.
    if (!this.risk) {
      return;
    }
    if (!this.risk.get('id')) {
      this.risk.deleteRecord();
      this.risk = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.risk);
    }
    this.toggleProperty('riskDialogOpen');
  }

  @action
  closeMitigationDialog() {
    // See closeDialog's comment -- same double-fire guard.
    if (!this.mitigation) {
      return;
    }
    if (!this.mitigation.get('id')) {
      this.mitigation.deleteRecord();
      this.mitigation = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.mitigation);
    }
    this.toggleProperty('mitigationDialogOpen');
  }

  @action
  openDialog(type) {
    if (type === 'mitigation') {
      if (!this.editMitigation) {
        this.mitigation = this.store.createRecord('events/mitigation', {
          schoolId: this.get('session.school'),
        });
      }
      this.toggleProperty('mitigationDialogOpen');
      return;
    }
    if (type === 'risk') {
      if (!this.editRisk) {
        this.risk = this.store.createRecord('events/risk', {
          schoolId: this.get('session.school'),
        });
        this.toggleProperty('riskDialogOpen');
        return;
      }
      // The bound `risk` is an existing record, so its `mitigationIds`
      // hasMany may not have resolved yet. sortedRemainingMitigations
      // guards against that (see its own comment), but waiting here too
      // means the dialog doesn't open showing a briefly-empty "remaining
      // mitigations" list before the relationship settles.
      this.risk.mitigations.then(() => {
        if (!this.isDestroying && !this.isDestroyed) {
          this.toggleProperty('riskDialogOpen');
        }
      });
      return;
    }
    if (this.type === 'template') {
      if (!this.editTemplate) {
        this.template = this.store.createRecord('events/template', {
          schoolId: this.get('session.school'),
        });
      }
      this.toggleProperty('templateDialogOpen');
      return;
    }
    if (this.edit) {
      let props = {};
      let inputType = this.permission.get('inputType');
      props.inputType = this.getInputType(inputType);
      if (inputType === InputType.NUMBER) {
        props.numericType = this.getNumericType(
          this.permission.get('dataType'),
        );
      }
      if (inputType === InputType.SELECT) {
        props.selectDataType = findBy(
          this.selectDataTypes,
          'value',
          this.permission.get('dataType'),
        );
      } else {
        props.selectDataType = findBy(
          this.selectDataTypes,
          'value',
          DataType.STRING,
        );
      }
      this.setProperties(props);
    } else {
      this.permission = this.store.createRecord('events/permission', {
        dataType: getDefaultDatatype(this.inputTypes[0]),
        inputType: this.inputTypes[0].value,
        schoolId: this.get('session.school'),
        type: this.type,
      });
      this.inputType = this.inputTypes[0];
      this.numericType = this.numericTypes[0];
      this.selectDataType = this.selectDataTypes[0];
      this.newOptionLabel = '';
      this.newOptionValue = '';
    }
    this.toggleProperty('dialogOpen');
  }

  @action
  savePermission() {
    this.saveInProgress = true;
    this.errorMessage = '';

    switch (this.get('inputType.value')) {
      case InputType.NUMBER:
        this.permission.set('dataType', this.get('numericType.value'));
        break;
      case InputType.SELECT:
        this.permission.set('dataType', this.selectDataType.value);
        break;
    }

    return this.permission
      .save()
      .then((permission) => {
        if (this.addPermission) {
          this.addPermission(permission);
        }
        this.dialogOpen = false;
      })
      .catch((err) => {
        this.handleError(err);
        this.permission.rollbackAttributes();
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  @action
  selectPermission(permission) {
    // Tidy up
    this.permission.deleteRecord();
    this.permission = null;
    // Add selected permission
    if (this.addPermission) {
      this.addPermission(permission);
    }
    this.dialogOpen = false;
  }

  @action
  saveTemplate() {
    this.saveInProgress = true;
    this.errorMessage = '';

    return this.template
      .save()
      .then(() => {
        this.templateDialogOpen = false;
      })
      .catch((err) => {
        this.handleError(err);
        this.template.rollbackAttributes();
      })
      .finally(() => {
        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);
        this.saveInProgress = false;
      });
  }

  @action
  saveRisk() {
    this.saveInProgress = true;
    this.errorMessage = '';

    return this.risk
      .save()
      .then(() => {
        this.onRiskSaved(this.risk);
        if (!this.editRisk) {
          // delete bypasses Ember's reactivity system entirely -- nothing
          // depending on `risk` (sortedRemainingMitigations, the dialog's
          // own bindings) gets notified, leaving stale state behind for
          // the next "add risk" click. set() is what every other reset
          // in this file already uses (see closeRiskDialog etc.).
          this.risk = null;
        }
        this.riskDialogOpen = false;
      })
      .catch((err) => {
        this.handleError(err);
        this.risk.rollbackAttributes();
      })
      .finally(() => {
        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);
        this.saveInProgress = false;
      });
  }

  @action
  saveMitigation() {
    this.saveInProgress = true;
    this.errorMessage = '';

    return this.mitigation
      .save()
      .then(async (mitigation) => {
        if (this.risk) {
          // resolve the async hasMany relationship upfront.
          (await this.risk.mitigations).push(mitigation);
        }
        this.mitigationDialogOpen = false;
      })
      .catch((err) => {
        this.handleError(err);
        this.mitigation.rollbackAttributes();
      })
      .finally(() => {
        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);
        this.saveInProgress = false;
        this.notifyPropertyChange('sortedRemainingMitigations');
      });
  }

  @action
  async addMitigation(mitigation) {
    // resolve the async hasMany relationship upfront.
    (await this.risk.mitigations).push(mitigation);
  }

  @action
  async removeMitigation(mitigation) {
    // resolve the async hasMany relationship upfront.
    let mitigations = await this.risk.mitigations;
    let index = mitigations.indexOf(mitigation);
    if (index !== -1) {
      mitigations.splice(index, 1);
    }
  }

  @action
  addOption() {
    if (isEmpty(this.get('permission.options'))) {
      // Plain array, not A(): options is a @fragmentArray attribute, and
      // Ember Data always wraps whatever it's set to in a real
      // FragmentArray regardless of the input's own type (verified
      // against the installed ember-data-model-fragments source earlier
      // in this migration).
      this.permission.set('options', []);
    }
    this.permission.options.pushObject({
      value: this.newOptionValue,
      label: this.newOptionLabel,
    });
    this.newOptionValue = '';
    this.newOptionLabel = '';
  }

  @action
  removeOption(option) {
    this.permission.options.removeObject(option);
  }

  @action
  maybeMutBoth(a, b, context, newValue) {
    if (context.get(a) === context.get(b)) {
      context.set(b, newValue);
    }
    context.set(a, newValue);
  }

  @action
  optionSortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // options is a @fragmentArray; .splice() comes from the
    // fragment-array-mutable initializer (StatefulArray only has
    // .replace() natively).
    const [item] = sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  getInputType(inputType) {
    let types = this.inputTypes;
    return findBy(types, 'value', inputType);
  }

  getNumericType(dataType) {
    let types = this.numericTypes;
    return findBy(types, 'value', dataType);
  }

  handleError(err) {
    if (!isEmpty(err.errors)) {
      if (!isEmpty(err.errors.msg)) {
        this.errorMessage = err.errors.msg;
      } else if (!isEmpty(err.errors[0])) {
        let error = err.errors[0];
        if (!isEmpty(error.detail)) {
          this.errorMessage = error.detail;
        }
      } else {
        this.errorMessage = err.message;
      }
    }
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.isMobile = isMobile();

    // TinyMCE autosave plugin
    // Manage the button's state on initial insertion
    manageAutosaveDraftsButton(this.editorId);
  }

  willUpdate() {
    super.willUpdate(...arguments);
    // TinyMCE autosave plugin
    // Manage the button's state on initial update
    manageAutosaveDraftsButton(this.editorId);
  }

  didUpdate() {
    super.didUpdate(...arguments);

    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
    this.timer = later(
      this,
      function () {
        manageAutosaveDraftsButton(this.editorId);
      },
      3000,
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
  }
}
